import { isBefore } from 'date-fns'
import { Linking } from 'react-native'
import useSWR from 'swr'
import { proxy, snapshot } from 'valtio'

import { Subscription } from '../../@types'
import { subscriptionService } from '../api/subscription.service'
import { PatientSubscriptionPaymentStatus, PatientSubscriptionStatus } from '../types'

interface SubscriptionState {
  subscription: Subscription | null
  isLoading: boolean
  isPolling: boolean
  error: string | null
}

export const TWO_SECONDS = 2 * 1000

const isPaymentCompleted = (status?: PatientSubscriptionPaymentStatus): boolean => {
  return (
    status === PatientSubscriptionPaymentStatus.FAILED ||
    status === PatientSubscriptionPaymentStatus.SUCCEEDED ||
    status === PatientSubscriptionPaymentStatus.EXPIRED ||
    status === PatientSubscriptionPaymentStatus.CANCELED
  )
}

const isSubscriptionCompleted = (status?: string): boolean => {
  return status !== PatientSubscriptionStatus.PENDING
}

const subscriptionState = proxy<SubscriptionState>({
  subscription: null,
  isLoading: false,
  isPolling: false,
  error: null,
})

const setIsLoading = (value: boolean) => {
  subscriptionState.isLoading = value
}

const setIsPolling = (value: boolean) => {
  subscriptionState.isPolling = value
}

const clearState = () => {
  subscriptionState.subscription = null
  subscriptionState.isLoading = false
  subscriptionState.isPolling = false
  subscriptionState.error = null
}

const isSubscriptionBookingAllowed = (): boolean => {
  const { subscription } = snapshot(subscriptionState)

  if (!subscription || !subscription.currentPeriodEnd) return false

  const isStillActive =
    isBefore(new Date(), new Date(subscription.currentPeriodEnd)) ||
    subscription.status === PatientSubscriptionStatus.ACTIVE ||
    subscription.status === PatientSubscriptionStatus.TRIALING

  return isStillActive
}

export const openUpdatePaymentMethodLink = (subscriptionId: string) => {
  const subscriptionUrl = `https://secure.monei.com/subscriptions/${subscriptionId}/activate`
  Linking.openURL(subscriptionUrl).catch(err => console.error('Failed to open URL:', err))
}

export const useSubscriptionState = (patientId?: number, subscriptionId?: number, onSuccess?: () => void) => {
  const shouldFetch = !!patientId && !!subscriptionId

  const { mutate } = useSWR(
    shouldFetch ? `/patients/${patientId}/subscription/${subscriptionId}` : null,
    async () => {
      const response = await subscriptionService.getSubscription(patientId!, subscriptionId!)
      if (response.ok) {
        subscriptionState.subscription = response.subscription || null
        subscriptionState.error = null
        return response.subscription
      } else {
        subscriptionState.subscription = null
        subscriptionState.error = response.message || 'Failed to fetch subscription'
        throw new Error(response.message)
      }
    },
    {
      revalidateOnFocus: true,
      revalidateIfStale: true,
      revalidateOnReconnect: true,
    }
  )

  const fetchSubscription = async () => {
    const state = snapshot(subscriptionState)

    if (patientId && state.subscription) {
      await mutate()
    }
  }

  // TODO: Change naming here based on current logic
  const pollSubscriptionUntilCompleted = async (intervalMs = TWO_SECONDS) => {
    const state = snapshot(subscriptionState)

    if (!patientId || !state.subscription) {
      console.warn('No polling started: No patientID nor subscription found')
      return
    }

    setIsPolling(true)

    const pollInterval = setInterval(async () => {
      try {
        if (!state?.subscription?.id) return
        const { subscription } = await subscriptionService.getSubscription(patientId!, state?.subscription?.id)
        subscriptionState.subscription = subscription || null

        const paymentCompleted = isPaymentCompleted(subscription?.lastPaymentStatus)
        const subscriptionCompleted = isSubscriptionCompleted(subscription?.status)

        // Stop polling if both conditions are met
        if (paymentCompleted && subscriptionCompleted) {
          clearInterval(pollInterval)
          onSuccess?.()
          setIsPolling(false)
        }
      } catch (error) {
        console.error('Error while polling subscription:', error)
        clearInterval(pollInterval)
        setIsPolling(false)
      }
    }, intervalMs)
  }

  // TODO: Pass plan ID when needed
  const subscribe = async (payload: { sessionId: string; planId?: number; lab?: string; promoCode?: string }) => {
    setIsLoading(true)
    subscriptionState.error = null
    try {
      const response = await subscriptionService.subscribe(patientId!, payload)
      if (response.ok) {
        subscriptionState.subscription = response.subscription || null
        await mutate()
        await pollSubscriptionUntilCompleted()
      } else {
        subscriptionState.error = response.message || 'Failed to subscribe'
      }
    } catch (err) {
      subscriptionState.error = (err as Error).message
    } finally {
      setIsLoading(false)
    }
  }

  const unsubscribe = async (payload: { cancelAtPeriodEnd: boolean }) => {
    setIsLoading(true)
    subscriptionState.error = null
    try {
      const response = await subscriptionService.unsubscribe(patientId!, payload)
      if (response.ok) {
        subscriptionState.subscription = response.subscription || null
        await mutate()
      } else {
        subscriptionState.error = response.message || 'Failed to unsubscribe'
      }
    } catch (err) {
      subscriptionState.error = (err as Error).message
    } finally {
      setIsLoading(false)
    }
  }

  // TODO: Double check this as we need the loading to be predictable.
  // useEffect(() => {
  //   if (error) {
  //     subscriptionState.error = error.message
  //   }

  //   setIsLoading(!data && !error && isValidating)
  // }, [data, error, isValidating])

  return {
    fetchSubscription,
    subscribe,
    unsubscribe,
    clearState,
    isSubscriptionBookingAllowed,
    state: subscriptionState,
  }
}
